/* eslint-disable prefer-template */
import './MultiChoiceForm.scss';

import PropTypes from 'prop-types';
import React from 'react';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Tick from 'components/ui/Tick';
import { ReactComponent as AlertIcon } from 'assets/icons/alert.svg';
import cx from 'utils/cx';
import GenericForm from './GenericForm';

const MultiChoiceFormColumn = ({ field, options, type, column, disabled }) => {
  const onChange = ({ target: { value } }) => {
    if (type === 'radio') {
      field?.onChange(value + '');
    }
    if (type === 'checkbox') {
      const isChecked =
        (field.value || []).indexOf(+value) !== -1 ||
        (field.value || []).indexOf(value + '') !== -1;
      if (isChecked) {
        field.onChange(
          (field.value || []).filter((val) => val + '' !== value + ''),
        );
      } else {
        field.onChange([...(field.value || []), value + '']);
      }
    }
  };

  const getComponent = ({ value, component }) => {
    if (!component) {
      return null;
    }

    const checked =
      type === 'radio'
        ? field.value === value
        : ((field.value || []).indexOf(+value) !== -1 ||
            (field.value || []).indexOf(value + '')) !== -1;
    if (!checked) {
      return null;
    }

    return typeof component === 'function' ? component() : component;
  };

  return (
    <ul>
      {options.map((option, i) => (
        <li
          key={option.value}
          style={{
            minWidth: `${(101 * (option.column || 1)) / (column + 1)}%`,
            flex: getComponent(option) ? 1 : 'initial',
            marginBottom: i < options.length - column ? 8 : 0,
            paddingRight:
              (i + 1) % column !== 0 && i !== options.length - 1 ? 20 : 0,
          }}
        >
          <Tick
            type={type}
            value={option.value}
            label={option.label}
            subLabel={option.subLabel}
            disabled={disabled}
            checked={
              type === 'radio'
                ? field.value === `${option?.value}`
                : ((field.value || []).indexOf(+option.value) !== -1 ||
                    (field.value || []).indexOf(option.value + '')) !== -1
            }
            onChange={onChange}
          />
          {getComponent(option)}
        </li>
      ))}
    </ul>
  );
};

MultiChoiceFormColumn.propTypes = {
  field: PropTypes.object.isRequired,
  options: PropTypes.array.isRequired,
  type: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  column: PropTypes.number.isRequired,
};

const MultiChoiceForm = ({
  title,
  multiLabel,
  required,
  name,
  options,
  type,
  className,
  children,
  column,
  disabled,
}) => {
  const { t } = useTranslation('common');
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    rules: { required },
    defaultValue:
      type === 'radio' ? options.find((opt) => opt.default)?.value : [],
  });

  const getErrorMessage = () => (
    <div className="form-message-wrapper">
      <AlertIcon />
      <span>{t('isRequired')}</span>
    </div>
  );

  return (
    <GenericForm
      title={title}
      subTitle={multiLabel ? `(${t('multiChoice')})` : null}
      required={required}
      className={cx(className, 'multi-choice-form-sec')}
      ref={field.ref}
    >
      {!!options?.length && (
        <div className="multi-choice-form">
          <MultiChoiceFormColumn
            name={name}
            required={required}
            options={options}
            type={type}
            field={field}
            column={column}
            disabled={disabled}
          />
          {error && getErrorMessage()}
        </div>
      )}
      {children}
    </GenericForm>
  );
};

MultiChoiceForm.propTypes = {
  title: PropTypes.string,
  multiLabel: PropTypes.bool,
  required: PropTypes.bool,
  name: PropTypes.string.isRequired,
  options: PropTypes.array,
  type: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  column: PropTypes.number,
  disabled: PropTypes.bool,
};

MultiChoiceForm.defaultProps = {
  title: '',
  multiLabel: false,
  required: false,
  options: [],
  type: 'checkbox',
  className: null,
  children: null,
  column: 1,
  disabled: false,
};

export default MultiChoiceForm;
